import { Box3, PerspectiveCamera, Sphere, Vector3 } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import { disposeObject } from "../../../render/utilities/disposeObject";
import { Lighting } from "../../../render/Lighting";
import { Renderer } from "../../../render/Renderer";
import { VIEWER_LIGHTING } from "../../viewerConstants";
import { Game } from "src/api";
import { boundsToBox } from "src/client/human/game/board/utilities/boardCoordinates";
import { createAPTiles, loadAllPieces } from "./AllPiecesBoardTiles";
import { World } from "../../../render/world/World";
import { createTypedEventEmitter } from "src/shared/util";

const VIEW_HEIGHT = 10;
const CAMERA_PITCH_DEGREES = 55;
const CAMERA_YAW_DEGREES = 45;
const CAMERA_DISTANCE = 40;

export class AllPieceScene {

  public loadState = false;

  public readonly events = createTypedEventEmitter<{
    completed: void
  }>();

  private readonly world = new World();
  private readonly camera = new PerspectiveCamera(90, 1, 0.01, 200);
  private readonly renderer: Renderer;
  private readonly lighting: Lighting;
  private readonly controls: OrbitControls;
  private readonly tiles = createAPTiles();

  constructor(container: HTMLElement, background: string) {
    this.world.setBackgroundColor(background);

    this.camera.position.set(2, 1, 2);
    this.renderer = new Renderer(container, this.world, this.camera);
    this.world.scene.add(this.tiles);

    this.lighting = new Lighting(this.world, this.renderer, VIEWER_LIGHTING);

    this.controls = new OrbitControls(this.camera, this.renderer.canvas);
    this.controls.enableDamping = true;

    this.renderer.onFrame(() => this.controls.update());

    this.postConstruct();
  }

  showGame(game: Game) {
    const facts = game.board.facts();
    this.aimCameraAt(boundsToBox(facts.framed));
  }

  private async postConstruct() {
    await loadAllPieces(this.tiles).catch(console.error);
    this.loadState = true;
    this.events.emit("completed");
  }

  private aimCameraAt(box: Box3) {
    const sphere = box.getBoundingSphere(new Sphere());
    const polarAngle = ((90 - CAMERA_PITCH_DEGREES) * Math.PI) / 180;
    const yaw = (CAMERA_YAW_DEGREES * Math.PI) / 180;

    this.controls.target.copy(sphere.center);
    this.camera.position
      .copy(sphere.center)
      .add(
        new Vector3().setFromSphericalCoords(CAMERA_DISTANCE, polarAngle, yaw),
      );
    this.camera.zoom = VIEW_HEIGHT / (sphere.radius * 2);
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  dispose() {
    this.world.dispose();
    this.controls.dispose();
    this.lighting.dispose();
    this.renderer.dispose();
    disposeObject(this.tiles);
  }
}
